import { pipe } from 'fp-ts/lib/function';
import { useIntl } from 'react-intl';
import { ChunkType } from '~/components/molecules/settings/settings';
import { useConfig } from '~/core/configuration/configContext';

// we presume the shape of the message is:
// "Zobrazit <strong>{resultCount, plural, =1 {# výsledek} =2 {# výsledky} =3 {# výsledky} =4 {# výsledky} other {# výsledků}}</strong>",

// Zobrazit <strong>{resultCount, plural, =1 {# výsledek} =2 {# výsledky} =3 {# výsledky} =4 {# výsledky} other {# výsledků}}</strong>
type countOptions = 1 | 2 | 3 | 4 | 'other';
type countFormattingRegex = Record<countOptions, RegExp>;
type countFormattingOptions = Record<countOptions, string>;

const customFormattingForResultCount = (resultCount: number, options: countFormattingOptions): string => {
    if (resultCount > 0 && resultCount < 5) {
        return `${resultCount} ${options[resultCount as countOptions]}`;
    }
    return `${resultCount} ${options.other}`;
};

const customProductsFiltersMessage = ({
    productsResults,
    message,
}: {
    productsResults: number;
    message: string;
}): string => {
    return pipe(
        message,
        // replace the resultCount placeholder with the actual resultCount
        (messageStep1) => {
            const wholeBlockRegex = /\{resultCount.*?\}\}/g; // match the whole placeholder
            const countRegex = {
                1: /=1\s\{# (.*?)\}/g,
                2: /=2\s\{# (.*?)\}/g,
                3: /=3\s\{# (.*?)\}/g,
                4: /=4\s\{# (.*?)\}/g,
                other: /other\s\{#(.*?)\}/g,
            } satisfies countFormattingRegex;
            const countOptions = Object.keys(countRegex).reduce((acc, key) => {
                const regex = countRegex[key as countOptions];
                const result = regex.exec(messageStep1);
                const captureGroup = result?.[1] ?? '';
                return {
                    ...acc,
                    [key as countOptions]: captureGroup,
                };
            }, {} as countFormattingOptions);

            return messageStep1.replace(wholeBlockRegex, customFormattingForResultCount(productsResults, countOptions));
        }
    );
};

export const useProductsFiltersMessage = (productsResults: number) => {
    const intl = useIntl();
    const {
        translations: { search_results_overview },
    } = useConfig();
    const productFiltersMessage = (() => {
        // --- MESSAGE USING PLURAL INTERPOLATION ---
        // The problem is that on some sites, there is a situation where format JS interpolation is not working due to collision with other scripts like MicrosoftAjax.js
        // viz. https://jira.mediafactory.cz/browse/SRSERP-244
        // the workaround is to catch the error in the component and then use custom fallback solution
        // TODO: better, functional handling of this try catch using Either/Option
        let message = '';
        try {
            message = intl.formatMessage(
                {
                    id: 'search_results_overview',
                    defaultMessage:
                        'Zobrazit <strong>{resultCount, plural, =1 {# výsledek} =2 {# výsledky} =3 {# výsledky} =4 {# výsledky} other {# výsledků}}</strong>',
                },
                {
                    resultCount: productsResults,
                    strong: (chunks: ChunkType) => `<strong>${chunks}</strong>`,
                }
            );
        } catch (error) {
            console.warn(`Fallback message formatting for message id: "search_results_overview".`);
            try {
                message = customProductsFiltersMessage({ productsResults, message: search_results_overview });
            } catch (err: unknown) {
                console.warn(
                    `Fallback message formatting for message id: "search_results_overview" failed. Using static message.`
                );
                console.error(err);
                return `Zobrazit <strong>${productsResults} výsledků</strong>`;
            }
        }
        return message;
    })();
    return productFiltersMessage;
};
